import { useDraggable } from '@dnd-kit/core';

import type { IssueCard } from '@tasks/contracts';

import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { PRIORITY_COLOR_VAR } from '../../lib/types';
import { formatPoints } from '../../lib/format';
import { priorityLabels } from '../../i18n/enums';
import { cn } from '../../lib/cn';
import { GripVertical } from 'lucide-react';
import styles from './BacklogIssueRow.module.css';

export type BacklogIssueRowProps = {
  readonly issue: IssueCard;
  readonly isSelected: boolean;
  readonly onOpen: (key: string) => void;
  readonly onToggleSelect: (issueId: string, event: { readonly shiftKey: boolean; readonly metaKey: boolean }) => void;
};

/** Строка бэклога: высота 36 px (docs/06 §5.8), drag за всю строку. */
export function BacklogIssueRow({ issue, isSelected, onOpen, onToggleSelect }: BacklogIssueRowProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `issue:${issue.id}`,
    data: { card: issue },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(styles['row'], isSelected && styles['selected'], isDragging && styles['dragging'])}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
    >
      <input
        type="checkbox"
        checked={isSelected}
        aria-label={`Выбрать ${issue.key}`}
        onChange={() => undefined}
        onClick={(event) =>
          onToggleSelect(issue.id, { shiftKey: event.shiftKey, metaKey: event.metaKey || event.ctrlKey })
        }
      />
      <span aria-hidden className={styles['handle']} {...attributes} {...listeners}>
        <GripVertical size={14} />
      </span>
      <button type="button" className={styles['key']} onClick={() => onOpen(issue.key)}>
        {issue.key}
      </button>
      <button type="button" className={styles['summary']} onClick={() => onOpen(issue.key)}>
        {issue.summary}
      </button>

      {issue.labels.slice(0, 2).map((label) => (
        <Badge key={label}>{label}</Badge>
      ))}

      <span className={styles['priority']} title={priorityLabels[issue.priority]}>
        <span aria-hidden className={styles['priorityDot']} style={{ background: PRIORITY_COLOR_VAR[issue.priority] }} />
        {priorityLabels[issue.priority]}
      </span>

      <span className={styles['points']}>{issue.storyPoints === null ? '—' : `${formatPoints(issue.storyPoints)} sp`}</span>
      <Avatar user={issue.assignee} size="xs" />
    </div>
  );
}
